import { createContext, ReactNode } from 'react';

import { LOADING_STATUS } from 'components/ChatBV/constants';
import { useChatConversation } from 'components/ChatBV/hooks';
import {
  Message,
  LoadingStatus,
  GeneratedReport,
  MessageReportData,
  GenericReportResponse,
} from 'components/ChatBV/types';
import { ROLES } from 'components/SearchpalChatbot/utils/constants';

import { buildUserRequest } from '../utils/conversationUtils';

interface ChatContextType {
  messages: Message[];
  // eslint-disable-next-line no-unused-vars
  sendMessage: (content: string) => void;
  sendPersonFollowUpMessage: (
    // eslint-disable-next-line no-unused-vars
    name: string,
    // eslint-disable-next-line no-unused-vars
    simplifiedData: string,
    // eslint-disable-next-line no-unused-vars
    reports: MessageReportData
  ) => void;
  handleNewChat: () => void;
  generatedReports: Record<string, GeneratedReport>;
  loadingStatus: LoadingStatus;
}

export const ChatContext = createContext<ChatContextType>({
  messages: [],
  sendMessage: () => {},
  sendPersonFollowUpMessage: () => {},
  handleNewChat: () => {},
  generatedReports: {},
  loadingStatus: {
    message: '',
    subtitle: '',
  },
});

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const {
    handleConversation,
    userOrientedMessages,
    setUserOrientedMessages,
    lastContextId,
    setLastContextId,
    loadingStatus,
    setLoadingStatus,
    generatedReports,
    setGeneratedReports,
  } = useChatConversation();

  const sendMessage = async (content: string) => {
    const userMessage: Message = { content, role: ROLES.user };
    setUserOrientedMessages((prev) => [...prev, userMessage]);

    setLoadingStatus(LOADING_STATUS.THINKING);
    const response = await handleConversation(
      buildUserRequest(userMessage, lastContextId)
    );

    const assistantMessage: Message = {
      context_id: response.context_id,
      content: response.message,
      role: ROLES.assistant,
      response_id: response.response_id,
      reports: response.reports,
      teaser: response.teaser,
      params: response.params,
      executedFunctionCall: response.executedFunctionCall,
    };
    setTimeout(() => {
      setUserOrientedMessages((prev) => [...prev, assistantMessage]);
    }, 1500);
    setTimeout(() => {
      setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
  };

  const sendPersonFollowUpMessage = async (
    name: string,
    simplifiedData: string,
    reports: MessageReportData
  ) => {
    if (loadingStatus !== LOADING_STATUS.IDLE) {
      return;
    }
    const userMessage: Message = {
      content: `Tell me about ${name}`,
      role: ROLES.user,
    };

    setUserOrientedMessages((prev) => [...prev, userMessage]);

    setLoadingStatus(LOADING_STATUS.THINKING);
    const response = await handleConversation(
      buildUserRequest(
        {
          content: simplifiedData,
          role: ROLES.user,
        },
        lastContextId
      )
    );

    const assistantMessage: Message = {
      context_id: response.context_id,
      content: response.message,
      role: ROLES.assistant,
      response_id: response.response_id,
      reports: [
        {
          reportData: reports.reportData,
          type: reports.type,
          permalink: reports.permalink,
        },
      ],
      teaser: response.teaser,
      params: response.params,
      executedFunctionCall: response.executedFunctionCall,
    };
    setGeneratedReports((prev) => ({
      ...prev,
      [reports.permalink]: {
        reportData: reports.reportData as GenericReportResponse,
        searchType: reports.type as string,
        permalink: reports.permalink,
      },
    }));
    setTimeout(() => {
      setUserOrientedMessages((prev) => [...prev, assistantMessage]);
    }, 1500);
    setTimeout(() => {
      setLoadingStatus(LOADING_STATUS.IDLE);
    }, 1000);
  };

  const handleNewChat = () => {
    setUserOrientedMessages([]);
    setLastContextId(undefined);
  };

  return (
    <ChatContext.Provider
      value={{
        messages: userOrientedMessages,
        sendMessage,
        sendPersonFollowUpMessage,
        handleNewChat,
        generatedReports,
        loadingStatus,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
